import { useEffect, useRef, useState } from "react"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { isNumber } from "radash"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import {
  Customize as ApiCustomize,
  CustomizeOption as ApiCustomizeOption,
  RestaurantCartItem,
} from "@/lib/api/order-service"
import { getRestaurantItemDetail } from "@/lib/client/api/restaurant"
import { useAuthStore } from "@/lib/store/auth-store"
import { Category, CustomizeItem as DishCustomizeItem } from "@/lib/types"
import {
  Customize as CartCustomize,
  CustomizeOption as CartCustomizeOption,
  RestaurantItem,
} from "@/lib/types/cart"
import { useCartItems } from "@/features/cart/hooks/use-cart-detail"
import { addRestaurantItems } from "@/app/actions/order"

import { areCustomizationsEqual } from "../utils/cart-utils"

// Define interfaces for the hook
interface MenuItemDetailDialogProps {
  selectedItemId: number | null
  setSelectedItemId: (id: number | null) => void
  selectedItem: Category["items"][number] | null
  isEditing?: boolean
  restaurantId: string
  selectedItemIndex?: number | null
  setSelectedItemIndex?: (index: number | null) => void
}

interface FormValues {
  note: string
  [key: string]: any
}

export function useMenuItemDialog({
  selectedItemId,
  setSelectedItemId,
  selectedItem: placeholder,
  isEditing = false,
  restaurantId,
  selectedItemIndex,
  setSelectedItemIndex,
}: MenuItemDetailDialogProps) {
  const form = useForm<FormValues>({
    defaultValues: {
      note: "",
    },
  })
  const queryClient = useQueryClient()

  const scrollAreaRef = useRef<HTMLDivElement>(null)

  // Track form submission state
  const [hasSubmitted, setHasSubmitted] = useState(false)

  // Track invalid customization groups
  const [invalidCustomizations, setInvalidCustomizations] = useState<number[]>(
    []
  )

  // Get authenticated user
  const userId = useAuthStore((state) => state.user?.user_id)

  // Get existing cart for this restaurant
  const { restaurantCart } = useCartItems(restaurantId)

  // State for item quantity
  const [quantity, setQuantity] = useState(1)

  // Fetch item details
  const { data, isLoading } = useQuery({
    queryKey: ["dishDetail", selectedItemId],
    queryFn: () => getRestaurantItemDetail(selectedItemId!),
    enabled: !!selectedItemId,
    placeholderData: placeholder ? { data: placeholder } : keepPreviousData,
  })

  const selectedItem = data?.data
  const showSkeleton = isLoading && !placeholder

  // Find existing item in cart to pre-populate customizations
  useEffect(() => {
    // Only pre-populate when in editing mode
    if (!isEditing) {
      return
    }

    if (selectedItemId && restaurantCart?.restaurant_items) {
      // Find matching items in the cart

      let existingItems: RestaurantItem[] = []

      if (isNumber(selectedItemIndex)) {
        existingItems = [restaurantCart.restaurant_items[selectedItemIndex]]
      } else {
        existingItems = restaurantCart.restaurant_items.filter(
          (item: RestaurantItem) => item.item_id === selectedItemId
        )
      }

      // If the item exists in the cart
      if (existingItems.length > 0) {
        // Get the first instance to pre-populate (we could add logic to select a specific one)
        const existingItem = existingItems[0]

        // Set the note
        if (existingItem.note) {
          form.setValue("note", existingItem.note)
        }

        // Set the quantity
        setQuantity(existingItem.quantity)

        // Set the customizations if they exist
        if (
          existingItem.customizes &&
          existingItem.customizes.length > 0 &&
          selectedItem?.customize_item
        ) {
          // For each customization in the cart
          existingItem.customizes.forEach((customize: any) => {
            // Find the matching customization in the item detail
            const customizeItem = selectedItem.customize_item.find(
              (item: any) => item.customize_id === customize.id
            )

            if (customizeItem) {
              // For single selection customizations (radio)
              if (
                customizeItem.is_check_box === 0 &&
                customize.options.length === 1
              ) {
                // Set the value for radio button customizations
                form.setValue(
                  `customize-${customize.id}`,
                  customize.options[0].id
                )
              }
              // For multiple selection customizations (checkbox)
              else if (
                customizeItem.is_check_box === 1 &&
                customize.options.length > 0
              ) {
                // Set the values for checkbox customizations
                const optionIds = customize.options.map(
                  (option: any) => option.id
                )
                form.setValue(`customize-${customize.id}`, optionIds)
              }
            }
          })
        }
      }
    }
  }, [
    selectedItemId,
    restaurantCart,
    selectedItem,
    form,
    isEditing,
    selectedItemIndex,
  ])

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (selectedItemId === null) {
      setHasSubmitted(false)
      setInvalidCustomizations([])
      setQuantity(1)
      form.reset()
    }
  }, [selectedItemId, form])

  // Scroll to the first invalid customization
  useEffect(() => {
    if (invalidCustomizations.length > 0 && scrollAreaRef.current) {
      const firstInvalidId = invalidCustomizations[0]
      const invalidElement = document.getElementById(
        `customize-${firstInvalidId}`
      )

      if (invalidElement) {
        // Add offset to account for sticky header (approx 70px)
        const scrollContainer = scrollAreaRef.current.querySelector(
          "[data-radix-scroll-area-viewport]"
        )
        const elementTop = invalidElement.offsetTop

        if (scrollContainer) {
          // Scroll with offset to prevent the sticky header from overlapping the element
          scrollContainer.scrollTo({
            top: Math.max(0, elementTop - 100),
            behavior: "smooth",
          })
        }
      }
    }
  }, [invalidCustomizations])

  // Add to cart mutation
  const addToCartMutation = useMutation({
    mutationFn: async ({
      items,
      note,
      cartId,
    }: {
      items: any[]
      note: string
      cartId?: string
    }) => {
      if (!userId || !restaurantId) {
        throw new Error("Vui lòng đăng nhập để thực hiện hành động này.")
      }

      return addRestaurantItems(userId.toString(), restaurantId, items, {
        cartId,
      })
    },
    onSuccess: () => {
      // Close the dialog
      setSelectedItemId(null)
      setSelectedItemIndex?.(null)
      // Invalidate cart query to refresh data
      queryClient.invalidateQueries({ queryKey: ["cart"] })

      // Show success message
      toast.success("Thêm vào giỏ hàng thành công", {
        description: "Món ăn đã được thêm vào giỏ hàng",
      })
    },
    onError: (error) => {
      toast.error("Không thể thêm vào giỏ hàng", {
        description:
          error instanceof Error
            ? error.message
            : "Đã xảy ra lỗi khi thêm món ăn vào giỏ hàng",
      })
    },
  })

  // Handle form submission
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setHasSubmitted(true)

    if (!selectedItem) return

    // Validate customizations
    const newInvalidCustomizations: number[] = []
    selectedItem.customize_item?.forEach((dishCustomize: DishCustomizeItem) => {
      const selectedOptionsFromForm = form.getValues(
        `customize-${dishCustomize.customize_id}`
      )
      const minQuantity = dishCustomize.customize_item_lower_limit ?? 0
      const maxQuantity = dishCustomize.customize_item_limit
        ? dishCustomize.customize_item_limit > 0
          ? dishCustomize.customize_item_limit
          : 9999
        : 9999

      let currentSelectedCount = 0
      if (Array.isArray(selectedOptionsFromForm)) {
        currentSelectedCount = selectedOptionsFromForm.length
      } else if (selectedOptionsFromForm) {
        currentSelectedCount = 1
      }

      console.log(
        "currentSelectedCount",
        currentSelectedCount,
        minQuantity,
        maxQuantity
      )

      if (
        currentSelectedCount < minQuantity ||
        currentSelectedCount > maxQuantity
      ) {
        newInvalidCustomizations.push(dishCustomize.customize_id)
      }
    })

    setInvalidCustomizations(newInvalidCustomizations)

    if (newInvalidCustomizations.length > 0) {
      toast.error("Vui lòng chọn đủ số lượng cho các tuỳ chọn")
      return
    }

    // Prepare cartCustomizations (numeric IDs, for RestaurantItem structure)
    const cartCustomizations: CartCustomize[] =
      selectedItem.customize_item
        ?.map((dishCustomize: DishCustomizeItem): CartCustomize => {
          const selectedOptionsFromForm = form.getValues(
            `customize-${dishCustomize.customize_id}`
          )
          let chosenOptions: CartCustomizeOption[] = []

          const optionsSource = dishCustomize.customize_options

          if (Array.isArray(selectedOptionsFromForm)) {
            chosenOptions = selectedOptionsFromForm
              .map((optionId: number) => {
                const optionDetail = optionsSource?.find(
                  (o) => o.customize_option_id === optionId
                )
                if (!optionDetail) return null
                return {
                  id: optionDetail.customize_option_id,
                  name: optionDetail.customize_option_name,
                  price: optionDetail.customize_price || 0,
                  quantity: 1,
                }
              })
              .filter((opt): opt is CartCustomizeOption => opt !== null)
          } else if (selectedOptionsFromForm) {
            const optionId = selectedOptionsFromForm as number
            const optionDetail = optionsSource?.find(
              (o) => o.customize_option_id === optionId
            )
            if (optionDetail) {
              chosenOptions = [
                {
                  id: optionDetail.customize_option_id,
                  name: optionDetail.customize_option_name,
                  price: optionDetail.customize_price || 0,
                  quantity: 1,
                },
              ]
            }
          }
          return {
            id: dishCustomize.customize_id,
            name: dishCustomize.customize_item_name,
            options: chosenOptions,
          }
        })
        .filter(
          (cc: CartCustomize): cc is CartCustomize => cc.options.length > 0
        ) || []

    const newCartItemForComparison: RestaurantItem = {
      item_id: selectedItem.restaurant_item_id,
      quantity,
      price: selectedItem.price,
      old_price: selectedItem.old_price || 0,
      old_price_active: false,
      customizes: cartCustomizations,
      note: form.getValues("note") || "",
      modify_status: 0,
      order_item_id: 0,
      customise_object: JSON.stringify(cartCustomizations),
      item_name: selectedItem.item_name,
      customizes_name: cartCustomizations.map((c) => c.name).join(", "),
      alcoholic_status: selectedItem.alcoholic_status || 0,
    }

    const allCartItems: RestaurantItem[] = restaurantCart?.restaurant_items
      ? [...restaurantCart.restaurant_items]
      : []

    let itemFoundAndActionTaken = false

    // If we have a specific selectedItemIndex, we should always use that index to update/replace
    // the item at that exact position, rather than searching for a "matching" item
    let existingItemIndex = -1

    if (
      isNumber(selectedItemIndex) &&
      selectedItemIndex >= 0 &&
      selectedItemIndex < allCartItems.length
    ) {
      // When selectedItemIndex is provided, we always use it - this is an explicit edit of a specific cart item
      existingItemIndex = selectedItemIndex
    } else {
      // Otherwise, try to find an identical item based on item_id, customizations, price, and note
      existingItemIndex = allCartItems.findIndex(
        (cartItem) =>
          cartItem.item_id === newCartItemForComparison.item_id &&
          areCustomizationsEqual(
            cartItem.customizes,
            newCartItemForComparison.customizes
          ) &&
          cartItem.price === newCartItemForComparison.price &&
          cartItem.note === newCartItemForComparison.note
      )
    }

    if (existingItemIndex !== -1) {
      if (quantity === 0 && isEditing) {
        allCartItems.splice(existingItemIndex, 1)
      } else if (quantity > 0) {
        const existingCartItem = allCartItems[existingItemIndex]

        // If the user explicitly provided selectedItemIndex, we're editing a specific item
        // so we should always replace the quantity (and other details), not add to it
        const usingProvidedIndex =
          isNumber(selectedItemIndex) && selectedItemIndex >= 0

        const finalQuantity =
          isEditing || usingProvidedIndex
            ? quantity // Replace quantity if editing or using provided index
            : existingCartItem.quantity + quantity // Add to existing quantity if "adding to cart" (not editing)

        allCartItems[existingItemIndex] = {
          ...newCartItemForComparison,
          quantity: finalQuantity,
          note: newCartItemForComparison.note,
          modify_status: 1,
          order_item_id: existingCartItem.order_item_id,
        }
      }
      itemFoundAndActionTaken = true
    } else if (quantity > 0) {
      allCartItems.push(newCartItemForComparison)
      itemFoundAndActionTaken = true
    }

    if (itemFoundAndActionTaken) {
      const itemsForApi: RestaurantCartItem[] = allCartItems.map(
        (cartItem: RestaurantItem): RestaurantCartItem => ({
          item_id: cartItem.item_id.toString(),
          quantity: cartItem.quantity,
          price: cartItem.price,
          note: cartItem.note,
          customizes: cartItem.customizes?.map(
            (customize: CartCustomize): ApiCustomize => ({
              id: customize.id.toString(),
              name: customize.name,
              options: customize.options.map(
                (option: CartCustomizeOption): ApiCustomizeOption => ({
                  id: option.id.toString(),
                  name: option.name,
                  price: option.price,
                  quantity: option.quantity,
                })
              ),
            })
          ),
        })
      )

      addToCartMutation.mutate({
        items: itemsForApi,
        note: form.getValues("note") || "",
        cartId: restaurantCart?.cart_id?.toString(),
      })
    } else if (quantity === 0 && !isEditing && existingItemIndex === -1) {
      setSelectedItemId(null)
    } else if (quantity === 0 && isEditing && existingItemIndex === -1) {
      setSelectedItemId(null)
    }
  }

  // Calculate total price including customizations
  const calculateTotal = () => {
    if (!selectedItem) return 0

    // Base price
    let total = (selectedItem.price || 0) * quantity

    // Process customizations to add prices from form state
    selectedItem.customize_item?.forEach((item: DishCustomizeItem) => {
      // Radio button customizations
      if (item.is_check_box === 0) {
        const optionId = form.watch(`customize-${item.customize_id}`)
        if (optionId) {
          const option = item.customize_options?.find(
            (opt) => opt.customize_option_id === Number(optionId)
          )
          if (option) {
            total += (option.customize_price || 0) * quantity
          }
        }
      }
      // Checkbox customizations
      else {
        const selectedOptionIds =
          form.watch(`customize-${item.customize_id}`) || []
        if (Array.isArray(selectedOptionIds) && selectedOptionIds.length > 0) {
          selectedOptionIds.forEach((optionId) => {
            const option = item.customize_options?.find(
              (opt) => opt.customize_option_id === optionId
            )
            if (option) {
              total += (option.customize_price || 0) * quantity
            }
          })
        }
      }
    })

    return total
  }

  return {
    form,
    scrollAreaRef,
    hasSubmitted,
    invalidCustomizations,
    quantity,
    setQuantity,
    selectedItem,
    showSkeleton,
    handleSubmit,
    calculateTotal,
    addToCartMutation,
  }
}
